import { Link, useLocation } from 'react-router-dom';
import { ArrowDownIcon, LogoIcon, NotificationIcon, UserNavIcon } from '@src/svg';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '@src/store/hooks';
import Avatar from './avatar';
import {
  selectUsers,
  setIsOpenAccountSettings,
} from '@src/store/slices/users/usersSlice';
import { useForm } from '@src/hooks/useDirtyForm';
import { ControlledSlideButton } from '@src/uikit/FormItems/ControlledItems';
import { useTheme } from '@src/hooks/useTheme';
import { APP_VERSION } from '@src/utils/constants';
import { useOutsideClick } from '@src/hooks/useOutsideClick';
import CustomDropdown from '@src/uikit/CustomDropdown/CustomDropdown';
import { sessionSetter } from '@src/utils/storage';
import {
  handleReading,
  resetNotifications,
  selectNotification,
} from '@store/slices/notification/notification';
import { subscribeNotificationSync } from '@utils/notificationSync';
import AccountSettingsModal from '@src/modals/AccountSettingsModal/AccountSettingsModal';
import Breadcrumbs from '@component/Breadcrumbs/Breadcrumbs';
import NotificationDropdown from '@component/NotificationDropdown/NotificationDropdown';
import { UserConfigDropdown } from '@component/Users/UserConfigDropdown';
import { useGetNotificationQuery } from '@store/rtk/twilio';
import ToggleSwitchButton from '@uikit/FormItems/ToggleSwitchButton/ToggleSwitchButton';
import { useRideMemberViewMode } from '@hooks/useRideMemberViewMode';
import {
  isRideMemberViewMode,
  RideMemberViewMode,
} from '@component/Ride/helper';
import { useBreadcrumbEntityName } from '@component/Breadcrumbs/useBreadcrumbEntityName';
import { useDispatch } from 'react-redux';

const Header = () => {
  const { toggleTheme } = useTheme();
  const { pathname } = useLocation();
  const isContractorCreatePage = pathname === '/contractors/create';
  const entityName = useBreadcrumbEntityName();
  const forRide = pathname.includes('rides/information');
  const userInfo = useAppSelector(selectUsers);
  const dispatch = useDispatch();

  const { notifications } = useAppSelector(selectNotification);

  const [openModal, setOpenModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [showNotificationsDropdown, setShowNotificationsDropdown] =
    useState<boolean>(false);

  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const [notifAnimOpen, setNotifAnimOpen] = useState(false);
  const [showNotifOverlay, setShowNotifOverlay] = useState(false);

  const avatarRef = useRef<HTMLDivElement | null>(null);
  const notificationsRef = useRef<HTMLDivElement | null>(null);
  const { viewMode, setViewMode } = useRideMemberViewMode({ forRide });

  const handleRideMemberViewModeChange = (nextMode: string) => {
    if (!isRideMemberViewMode(nextMode)) return;

    setViewMode(nextMode as RideMemberViewMode);
  };

  const theme = localStorage.getItem('theme');

  type SearchType = {
    lightMode: boolean;
    search: string;
  };

  useGetNotificationQuery(
    { page: 0, size: 5 },
    { refetchOnMountOrArgChange: true }
  );

  const currentUserId = userInfo?.userInfo?.id;

  useEffect(
    () =>
      subscribeNotificationSync((message) => {
        if (!currentUserId || message.userId !== currentUserId) return;
        if (message.type === 'read') {
          dispatch(handleReading({ id: message.id }));
        } else if (message.type === 'delete_all') {
          dispatch(resetNotifications());
        }
      }),
    [currentUserId, dispatch]
  );

  useOutsideClick({
    ref: avatarRef,
    onOutsideClick: () => setShowUserDropdown(false),
  });

  useOutsideClick({
    ref: notificationsRef,
    onOutsideClick: () => {
      if (!isMobile) setShowNotificationsDropdown(false);
    },
  });

  const { control, reset } = useForm<SearchType>({
    defaultValues: {
      search: '',
      lightMode: false,
    },
  });

  useEffect(() => {
    reset({
      search: '',
      lightMode: theme === 'light',
    });
  }, [theme]);

  useEffect(() => {
    const syncBurger = () => {
      const stored = sessionSetter.getItem<boolean>('burgerNavToggler');
      setOpenMenu(stored ?? false);
    };

    syncBurger();
    window.addEventListener('burgerNavTogglerChange', syncBurger);
    return () =>
      window.removeEventListener('burgerNavTogglerChange', syncBurger);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 720);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isMobile && showNotificationsDropdown) {
      document.body.classList.add('lock-scroll');
    } else {
      document.body.classList.remove('lock-scroll');
    }

    return () => {
      document.body.classList.remove('lock-scroll');
    };
  }, [showNotificationsDropdown, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      setNotifAnimOpen(showNotificationsDropdown);
      setShowNotifOverlay(false);
      return;
    }

    if (showNotificationsDropdown) {
      setShowNotifOverlay(true);
      requestAnimationFrame(() => setNotifAnimOpen(true));
    } else {
      setNotifAnimOpen(false);
      const t = setTimeout(() => setShowNotifOverlay(false), 250);
      return () => clearTimeout(t);
    }
  }, [showNotificationsDropdown, isMobile]);

  const toggleNav = () => {
    const isMobileNow = window.innerWidth <= 1100;

    if (isMobileNow) {
      const current =
        sessionSetter.getItem<boolean>('burgerNavToggler') ?? false;
      sessionSetter.setItem('burgerNavToggler', !current);
      window.dispatchEvent(new Event('burgerNavTogglerChange'));
    } else {
      const current =
        sessionSetter.getItem<boolean>('sidebarCollapsed') ?? false;
      sessionSetter.setItem('sidebarCollapsed', !current);
      window.dispatchEvent(new Event('sidebarCollapsedChange'));
    }
  };

  const closeNotifications = () => setShowNotificationsDropdown(false);

  const options = useMemo(
    () => [
      {
        item: <div>{userInfo?.userInfo?.email}</div>,
        closeWhenClicked: false,
        onSelect: () => {},
      },
      {
        item: (
          <div className="mode_item">
            dark mode
            <ControlledSlideButton
              name="lightMode"
              control={control}
              onChange={toggleTheme}
            />
            light mode
          </div>
        ),
        closeWhenClicked: false,
        onSelect: () => {},
      },
    ],
    [userInfo?.userInfo?.email, control, toggleTheme]
  );

  return (
    <header
      className={`header ${isContractorCreatePage ? 'header--contractor-flow' : ''}`}
    >
      {userInfo.isOpenAccountSettings && (
        <AccountSettingsModal
          closeModal={() => dispatch(setIsOpenAccountSettings(false))}
        />
      )}

      <div className="header__container">
        <div className="media_logo">
          <Link to="/">
            <LogoIcon />
          </Link>
        </div>
        <div className="header__left">
          <div className="header_icons">
            <UserNavIcon onClick={toggleNav} />
          </div>
          <Breadcrumbs entityName={entityName} />
        </div>
        <div className="header__right">
          <div className="header__user">
            {forRide && (
              <div className="ride-member-view-switch-wrap">
                <ToggleSwitchButton
                  className="ride-member-view-switch"
                  dataTestId="ride-member-view-mode-switch"
                  value={viewMode}
                  left={{ label: 'Compact', value: 'COMPACT' }}
                  right={{ label: 'Detailed', value: 'DETAILED' }}
                  ariaLabel="Toggle member view mode"
                  onChange={handleRideMemberViewModeChange}
                  mode="select"
                />
              </div>
            )}
            <div className="notification" ref={notificationsRef}>
              <button
                className="header_notification_btn"
                onClick={() => setShowNotificationsDropdown((prev) => !prev)}
                type="button"
              >
                <NotificationIcon />

                {notifications.unreadCount > 0 && (
                  <span className="notif-count">
                    {notifications.unreadCount > 99
                      ? '99+'
                      : notifications.unreadCount}
                  </span>
                )}
              </button>

              {isMobile && showNotifOverlay && (
                <div className="notif-overlay" onClick={closeNotifications} />
              )}

              <NotificationDropdown
                isOpen={isMobile ? notifAnimOpen : showNotificationsDropdown}
                isMobile={isMobile}
                onClose={closeNotifications}
              />
            </div>

            {userInfo && (
              <div
                data-test-id="header-avatar"
                ref={avatarRef}
                className="header__avatar"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
              >
                <Avatar
                  userImage={userInfo?.userInfo?.image || ''}
                  userStatus={userInfo?.userInfo?.status}
                />
                {showUserDropdown && <UserConfigDropdown />}
                {APP_VERSION === 'full' && (
                  <CustomDropdown button={<ArrowDownIcon />} options={options} />
                )}
              </div>
            )}

            <button
              data-test-id="header-burger-menu-btn"
              className={`burger_menu_wrapper ${openMenu ? 'is-active' : ''}`}
              onClick={toggleNav}
              type="button"
            >
              <span />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
